// hooks/iframe/useMessageSync.js
import { useEffect, useRef, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const useMessageSync = (chatId, chatState, apiKey, isOpen, onNewMessages, onChatStateChange) => {
  const intervalRef = useRef(null);
  const stateCheckIntervalRef = useRef(null);
  const previousStateRef = useRef(chatState);

  // Função para buscar novas mensagens
  const fetchNewMessages = useCallback(async () => {
    if (!chatId || !isOpen) return;

    try {
      let headers = {
        'Content-Type': 'application/json',
      }

      if (apiKey.startsWith("pawsf")) {
        headers['X-API-KEY'] = apiKey
      } else {
        headers['Authorization'] = `Bearer ${apiKey}`
      }

      const response = await fetch(
        `${API_BASE_URL}/chats/${chatId}/messages?limit=50`,
        {
          headers: headers
        }
      );

      if (response.ok) {
        const data = await response.json();
        const messages = data.messages || [];

        onNewMessages(messages);
      }
    } catch (error) {
      console.error('Erro ao sincronizar mensagens:', error);
    }
  }, [chatId, apiKey, isOpen, onNewMessages]);

  // ✨ FUNÇÃO ATUALIZADA para verificar estado do chat periodicamente
  const fetchChatState = useCallback(async () => {
    if (!chatId) return;

    try {
      let headers = {
        'Content-Type': 'application/json',
      }

      if (apiKey.startsWith("pawsf")) {
        headers['X-API-KEY'] = apiKey
      } else {
        headers['Authorization'] = `Bearer ${apiKey}`
      }

      const response = await fetch(
        `${API_BASE_URL}/chats/${chatId}`,
        {
          headers: headers
        }
      );

      if (response.ok) {
        const chatData = await response.json();
        const newState = chatData.state;

        console.log('[State Sync] Estado atual do chat:', newState);

        // Se o estado mudou, notifica o componente pai
        if (newState && newState !== previousStateRef.current) {
          console.log('[State Sync] Estado mudou de', previousStateRef.current, 'para', newState);
          previousStateRef.current = newState;
          
          if (onChatStateChange) {
            onChatStateChange(newState);
          }

          // ✨ Se mudou para 'ended', buscar mensagens atualizadas
          if (newState === 'ended') {
            console.log('[State Sync] Chat encerrado, buscando mensagens finais...');
            await fetchNewMessages();
          }
        }
      }
    } catch (error) {
      console.error('Erro ao verificar estado do chat:', error);
    }
  }, [chatId, apiKey, onChatStateChange, fetchNewMessages]);

  // Iniciar/parar polling baseado no estado
  useEffect(() => {
    // Atualizar ref do estado atual
    previousStateRef.current = chatState;

    // Limpar intervalos anteriores se existirem
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (stateCheckIntervalRef.current) {
      clearInterval(stateCheckIntervalRef.current);
      stateCheckIntervalRef.current = null;
    }

    if (!chatId || !isOpen) return;

    // ✨ Polling de mensagens para estados específicos
    if (chatState === 'paused' || chatState === 'handoff') {
      console.log(`[Sync] Iniciando sincronização de mensagens para ${chatState}`);
      
      // Buscar imediatamente
      fetchNewMessages();
      
      // Configurar intervalo de 5 segundos
      intervalRef.current = setInterval(() => {
        fetchNewMessages();
      }, 5000);
    }

    // ✨ NOVO: Polling de estado do chat a cada 10 segundos para TODOS os estados (exceto ended)
    if (chatState !== 'ended') {
      console.log('[Sync] Iniciando verificação periódica de estado do chat');
      
      // Verificar imediatamente
      fetchChatState();
      
      // Configurar intervalo de 10 segundos para verificar mudança de estado
      stateCheckIntervalRef.current = setInterval(() => {
        fetchChatState();
      }, 10000);
    }

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      if (stateCheckIntervalRef.current) {
        clearInterval(stateCheckIntervalRef.current);
        stateCheckIntervalRef.current = null;
      }
    };
  }, [chatState, chatId, isOpen, fetchNewMessages, fetchChatState]);

  // Retornar funções para forçar sincronização manual se necessário
  return {
    syncNow: fetchNewMessages,
    checkStateNow: fetchChatState
  };
};